import { useState } from "react";

/**
 * The only file input in the app. The browser's own file button loses its look under Tailwind's
 * reset and reads as plain text (Martín, 2026-10-06 and again 2026-10-19: "no parece un botón").
 * A visible drop zone with a real button instead; the input stays in the page, visually hidden,
 * so keyboards, screen readers and the e2e scripts reach it by its label as before.
 * `file-picker.test.ts` fails if a screen goes back to a bare `type="file"`.
 */
export function FilePicker({
  file,
  onChange,
  accept,
  label,
  hint,
}: {
  file: File | null;
  onChange: (file: File | null) => void;
  accept: string;
  /** Accessible name of the hidden input; the e2e scripts find it by this. */
  label: string;
  /** Shown under the button while no file is chosen, e.g. "o arrastralo acá · PDF o foto". */
  hint: string;
}) {
  const [dragging, setDragging] = useState(false);
  return (
    <label
      className={`flex cursor-pointer flex-col items-center gap-2 rounded-md border-2 border-dashed px-3 py-5 text-center transition-colors focus-within:ring-2 focus-within:ring-brand-tint ${
        dragging
          ? "border-brand-primary bg-brand-tint"
          : "border-brand-line bg-white hover:bg-brand-tint"
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        onChange(e.dataTransfer.files?.[0] ?? null);
      }}
    >
      <input
        type="file"
        className="sr-only"
        accept={accept}
        onChange={(e) => {
          onChange(e.target.files?.[0] ?? null);
          // Lets the same file be chosen again after the screen clears it.
          e.target.value = "";
        }}
        aria-label={label}
      />
      <span className="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-white">
        {file ? "Cambiar archivo" : "Elegir archivo"}
      </span>
      {file ? (
        <span className="break-all text-sm font-medium">{file.name}</span>
      ) : (
        <span className="text-xs text-brand-muted">{hint}</span>
      )}
    </label>
  );
}
